import {
    PROMOS_HYDRATED,
    SET_PROMO_URL_CODE,
    SET_PROMO_ENTERED_CODE,
    CLEAR_PROMO_ENTERED_CODE,
} from './actionTypes';
import { fetchAllPromos } from '../promos/promoFirestore';
import { normaliseCode } from '../promos/promoTypes';
import { trackPromoActive, trackPromoCodeApplied } from '../utils/promoAnalytics';
import { resolveActivePromo } from '../promos/resolveActivePromo';

const URL_CODE_STORAGE_KEY = 'mapShapedPromoUrlCode';
const ENTERED_CODE_STORAGE_KEY = 'mapShapedPromoEnteredCode';

/**
 * Read the `?promo=` query parameter and persist it to sessionStorage so
 * it survives client-side navigation. Call once at app boot.
 *
 * URL precedence: a fresh `?promo=` in the URL always overrides whatever
 * was previously stored. Removing the param does NOT clear the stored
 * code — only explicit user action (clearPromoCode) clears it.
 */
export function captureUrlPromoCode() {
    return (dispatch) => {
        let urlCode = null;
        try {
            const params = new URLSearchParams(window.location.search);
            urlCode = normaliseCode(params.get('promo'));
            if (urlCode) {
                sessionStorage.setItem(URL_CODE_STORAGE_KEY, urlCode);
            } else {
                urlCode = normaliseCode(sessionStorage.getItem(URL_CODE_STORAGE_KEY));
            }
        } catch (e) {
            // SSR / disabled storage — silently fall through.
        }
        dispatch({ type: SET_PROMO_URL_CODE, urlCode });

        // Restore any code the visitor manually entered earlier this session.
        try {
            const entered = normaliseCode(sessionStorage.getItem(ENTERED_CODE_STORAGE_KEY));
            if (entered) {
                dispatch({ type: SET_PROMO_ENTERED_CODE, enteredCode: entered });
            }
        } catch (e) { /* noop */ }
    };
}

/**
 * Load the full /promos collection from Firestore. Cheap — there will
 * never be more than a handful of active campaigns at a time. Fires
 * once on boot.
 *
 * After hydration, if a promo resolves to active we fire a one-time
 * `promo_active` analytics ping (GA + FB + Pinterest).
 */
export function hydratePromos() {
    return async (dispatch, getState) => {
        try {
            const promos = await fetchAllPromos();
            dispatch({ type: PROMOS_HYDRATED, promos });

            const state = getState();
            const active = resolveActivePromo({
                promos,
                now: Date.now(),
                urlCode: state.promos?.urlCode,
                enteredCode: state.promos?.enteredCode,
            });
            if (active) {
                trackPromoActive({
                    promoId: active.id,
                    code: active.code || null,
                    kind: active.kind,
                    discountPercent: active.discountPercent,
                });
            }
        } catch (e) {
            // Promo failure must never break the storefront. Log and continue
            // — the resolver will simply return null and every product opens
            // at its base SKU.
            // eslint-disable-next-line no-console
            console.warn('hydratePromos failed:', e);
            dispatch({ type: PROMOS_HYDRATED, promos: [] });
        }
    };
}

/**
 * The visitor typed a code into the PromoCodeBar and hit Apply. We don't
 * validate against the promo list here — the resolver will simply ignore
 * codes that don't match anything, so the UI can show "Sorry, that code
 * isn't active" purely from selector output.
 */
export function applyPromoCode(rawCode) {
    return (dispatch, getState) => {
        const enteredCode = normaliseCode(rawCode);
        dispatch({ type: SET_PROMO_ENTERED_CODE, enteredCode });
        try {
            if (enteredCode) {
                sessionStorage.setItem(ENTERED_CODE_STORAGE_KEY, enteredCode);
            } else {
                sessionStorage.removeItem(ENTERED_CODE_STORAGE_KEY);
            }
        } catch (e) { /* noop */ }

        const state = getState();
        const active = resolveActivePromo({
            promos: state.promos?.list || [],
            now: Date.now(),
            urlCode: state.promos?.urlCode,
            enteredCode,
        });
        trackPromoCodeApplied({
            code: enteredCode,
            matched: Boolean(active),
            discountPercent: active ? active.discountPercent : 0,
        });
    };
}

export function clearPromoCode() {
    return (dispatch) => {
        dispatch({ type: CLEAR_PROMO_ENTERED_CODE });
        try {
            sessionStorage.removeItem(ENTERED_CODE_STORAGE_KEY);
            sessionStorage.removeItem(URL_CODE_STORAGE_KEY);
        } catch (e) { /* noop */ }
        dispatch({ type: SET_PROMO_URL_CODE, urlCode: null });
    };
}
